import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Search, Trash2, Eye, RotateCcw, AlertTriangle } from "lucide-react";
import Plate from "@/components/Plate";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Corbeille() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/corbeille/ventes");
      setItems(data);
    } catch (err) {
      console.warn("Load trash failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (v) => {
    if (!window.confirm(`Restaurer la vente ${v.plaque} (${v.marque} ${v.modele}) ?`)) return;
    try {
      await api.post(`/ventes/${v.id}/restore`);
      toast.success("Vente restaurée");
      load();
    } catch {
      toast.error("Erreur lors de la restauration");
    }
  };

  const hardDelete = async (v) => {
    if (!window.confirm(`⚠️ SUPPRESSION DÉFINITIVE\n\nVente ${v.plaque} (${v.marque} ${v.modele})\n\nCette action supprimera également TOUS les paiements et interventions liés. C'est IRRÉVERSIBLE.\n\nContinuer ?`)) return;
    const typed = window.prompt('Tapez "SUPPRIMER" pour confirmer définitivement :');
    if (typed !== "SUPPRIMER") {
      toast.error("Confirmation annulée");
      return;
    }
    try {
      await api.delete(`/ventes/${v.id}/hard`);
      toast.success("Vente supprimée définitivement");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    const cleaned = t.replace(/[-\s]/g, "");
    return items.filter(
      (v) =>
        (v.plaque || "").toLowerCase().includes(cleaned) ||
        (v.client_nom || "").toLowerCase().includes(t) ||
        (v.marque || "").toLowerCase().includes(t) ||
        (v.modele || "").toLowerCase().includes(t),
    );
  }, [items, q]);

  return (
    <div className="space-y-6" data-testid="corbeille-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-amber-700 font-semibold flex items-center gap-2">
            <Trash2 size={12} /> Archives · Corbeille
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Corbeille</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Ventes supprimées récemment. Elles seront <strong>définitivement effacées au bout de 7 jours</strong>
            {" "}(paiements et interventions liés inclus).
            Restaurez-les avant si nécessaire.
          </p>
        </div>
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="corbeille-search"
            type="search"
            name="corbeille-search"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="pl-9 rounded-sm"
          />
        </div>
      </header>

      <Card className="rounded-sm border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-amber-50/40 text-left text-xs uppercase tracking-wider text-amber-800">
            <tr>
              <th className="px-6 py-3">Actions</th>
              <th className="px-6 py-3">Supprimée le</th>
              <th className="px-6 py-3">Jours restants</th>
              <th className="px-6 py-3">Plaque</th>
              <th className="px-6 py-3">Véhicule</th>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3 text-right">Prix vente</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground" data-testid="corbeille-empty">
                Corbeille vide.
              </td></tr>
            )}
            {filtered.map((v) => {
              const daysLeft = v.days_left ?? 7;
              const urgent = daysLeft < 2;
              return (
                <tr key={v.id} data-testid={`corbeille-row-${v.plaque}`} className="border-t hover:bg-amber-50/30">
                  <td className="px-6 py-3 whitespace-nowrap">
                    <button
                      onClick={() => restore(v)}
                      data-testid={`corbeille-restore-${v.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm font-semibold mr-1"
                    >
                      <RotateCcw size={12} /> Restaurer
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => hardDelete(v)}
                        data-testid={`corbeille-hard-${v.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-sm font-semibold"
                      >
                        <Trash2 size={12} /> Supprimer
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{dateFR(v.deleted_at)}</td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase tracking-wider ${
                      urgent ? "bg-red-100 text-red-800 border border-red-300" : "bg-slate-100 text-slate-700 border border-slate-200"
                    }`}>
                      {urgent && <AlertTriangle size={11} />}
                      {daysLeft} j
                    </span>
                  </td>
                  <td className="px-6 py-3"><Plate value={v.plaque} small /></td>
                  <td className="px-6 py-3 font-medium">{v.marque} {v.modele} {v.annee ? `(${v.annee})` : ""}</td>
                  <td className="px-6 py-3">{v.client_nom}</td>
                  <td className="px-6 py-3 text-right font-mono text-muted-foreground">{money(v.prix_vente)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <p className="text-[11px] text-muted-foreground">
        💡 La purge automatique a lieu à chaque consultation. Une vente supprimée depuis plus de 7 jours est
        effacée définitivement avec tous ses paiements et interventions.
      </p>
    </div>
  );
}
